/**
 * The steps of a consolidation's run (TC-S5-GUIDE; Pedro 2026-11-01, G1–G4 "yes to all 3 recs"):
 *
 *   1 Securities — what blocks a trade (no price → type it; no portfolio → choose it), each shown on its row;
 *   2 Split      — "Keep suggested securities trades and check cash", or "Optimize further (according to
 *                  trade rules)" (the Optimize split window);
 *   3 Cash       — on the split chosen: below zero / below a portfolio's minimum cash must be fixed or
 *                  accepted; after a model, each currency off the model's % is shown, accepted by the same tick;
 *   4 Save       — enabled only when 1–3 are done (the Save button says which step is open).
 *
 * TC-S5-CLUTTER (Pedro 2026-11-01: "the screen is filled with warnings and instructions and the portfolio
 * and trades table almost can't be seen … the next action needed from the user [must be] evident"; "let's
 * try it"): ONE line by default — the four steps, then "Next:" with the one action to take now and its
 * button(s); the step's full list opens on demand (All n ▾), in a box of about five lines that scrolls.
 * Presentational: the trade mode works out each step and passes it in.
 */
import { useState } from 'react';
import { Anchor, Badge, Box, Button, Checkbox, Group, Paper, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAdjustmentsHorizontal, IconCheck, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

export interface GuideItem { key: string | null; msg: string }
export interface GuideProps {
  /** The step whose detail is open (1–4). */
  step: number;
  onStep: (n: number) => void;
  sec: { items: GuideItem[]; onShow: (key: string) => void };
  /** keepWhy: why Keep is greyed (TC-S5-STEP1: a security left to the optimizer at step 1). */
  split: { state: 'none' | 'kept' | 'optimized'; why: string | null; keepWhy: string | null; onKeep: () => void; onOptimize: () => void;
    optimizedNote: string | null };
  cash: { ready: boolean; hard: string[]; soft: string[]; accepted: boolean; onAccept: (on: boolean) => void; onSuggest: () => void;
    suggestLabel: string;
    /** Why the suggest button is greyed (no transfer can fix what is left); null: enabled. */
    suggestWhy: string | null;
    /** The model plan's cash transfers, not applied yet (one line each); [] when none or used. */
    plan: string[] };
  save: { why: string | null };
}

type Status = { done: boolean; label: string; color: string };

export function RunGuide({ step, onStep, sec, split, cash, save }: GuideProps) {
  const [open, setOpen] = useState(false);
  const cashN = cash.hard.length + cash.soft.length;
  const statuses: Status[] = [
    sec.items.length === 0 ? { done: true, label: 'Done', color: 'green' }
      : { done: false, label: `${sec.items.length} to fix`, color: 'red' },
    split.state === 'kept' ? { done: true, label: 'Kept', color: 'green' }
      : split.state === 'optimized' ? { done: true, label: 'Optimized', color: 'green' }
        : { done: false, label: split.why ? 'Waiting' : 'To choose', color: split.why ? 'gray' : 'blue' },
    !cash.ready ? { done: false, label: 'Waiting', color: 'gray' }
      : cashN === 0 ? { done: true, label: 'Done', color: 'green' }
        : cash.accepted ? { done: true, label: 'Accepted', color: 'green' }
          : { done: false, label: `${cashN} to check`, color: cash.hard.length ? 'red' : 'orange' },
    save.why ? { done: false, label: 'Not yet', color: 'gray' } : { done: true, label: 'Ready', color: 'green' },
  ];
  // "Split" alone was not clear (Pedro 2026-11-01): the step decides how each trade is split across the portfolios.
  const titles = ['Securities', 'Split by portfolio', 'Cash', 'Save'];
  const listN = step === 1 ? sec.items.length : step === 3 && cash.ready ? cashN : 0;

  // ── the line says what to do next; the step's buttons sit on the row just under it, for every step
  // (Pedro 2026-11-01: step 2's options "under the 1-4 line, to be immediately visible"; then "again, the
  // options for step 3 should not appear on the far right, but rather where the ones for step 2 did") ──
  // The list's toggle sits in the Next text itself (Pedro 2026-11-02: "Next: Fix the cash (see all 5
  // issues) or Accept it (below):").
  const listToggle = listN > 1 && (
    <Anchor component="button" size="xs" fw={700} onClick={() => setOpen((o) => !o)} style={{ verticalAlign: 'baseline' }}>
      ({open ? 'hide the list' : `see all ${listN} issues`}{open ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />})
    </Anchor>
  );
  let next: React.ReactNode;
  let actions: React.ReactNode = null;
  if (step === 1) {
    const first = sec.items[0];
    next = !first ? <Text size="xs" c="dimmed">Every trade has a price and a portfolio.</Text>
      : <Text size="xs" c="red.8" truncate style={{ flex: 1, minWidth: 0 }}>
        <b>Settle each security</b> {listToggle}<b>:</b> {first.msg}
      </Text>;
    if (first?.key) actions = <Button size="xs" variant="light" onClick={() => first.key && sec.onShow(first.key)}>Show this row</Button>;
  } else if (step === 2) {
    next = split.why ? <Text size="xs" c="dimmed" style={{ flex: 1 }}>{split.why}</Text>
      : <Text size="xs" fw={split.state === 'none' ? 700 : 400} c={split.state === 'none' ? 'blue.8' : undefined} style={{ flex: 1, minWidth: 0 }} truncate>
        {split.state === 'none' ? (split.keepWhy ? 'Optimize below: some securities were left to the optimizer to choose their portfolio.'
          : 'Choose ONE of the two options below: how the trades are split across the portfolios.')
          : split.optimizedNote ?? 'Split kept (you can still optimize it below).'}
      </Text>;
    if (!split.why) actions = (<>
      <Tooltip withArrow multiline w={320} label={split.keepWhy ?? 'Each trade stays split as it is (by holdings, or as you typed it); then step 3 checks the cash.'}>
        {/* the span keeps the tooltip on a greyed button */}
        <span><Button size="xs" variant={split.state === 'kept' ? 'filled' : 'light'} leftSection={split.state === 'kept' ? <IconCheck size={14} /> : undefined}
          disabled={!!split.keepWhy} onClick={split.onKeep}>Keep suggested securities trades and check cash</Button></span>
      </Tooltip>
      <Text size="xs" c="dimmed">or</Text>
      <Button size="xs" color="grape" variant={split.state === 'optimized' ? 'filled' : 'light'}
        leftSection={split.state === 'optimized' ? <IconCheck size={14} /> : <IconAdjustmentsHorizontal size={14} />}
        onClick={split.onOptimize}>Optimize further (according to trade rules)…</Button>
    </>);
  } else if (step === 3) {
    const first = cash.hard[0] ?? cash.soft[0];
    next = !cash.ready ? <Text size="xs" c="dimmed">Choose the split at step 2 first: the cash depends on it.</Text>
      : !first ? <Text size="xs" c="dimmed">No cash issue: no account below zero, no portfolio below its minimum (and, after a model, every currency at the model's %).</Text>
        : <Text size="xs" c={cash.hard.length ? 'red.8' : 'orange.8'} truncate style={{ flex: 1, minWidth: 0 }}>
          <b>Fix the cash</b> {listToggle} <b>or accept it (below):</b> {first}
        </Text>;
    if (cash.ready && first) actions = (<>
      <Tooltip withArrow multiline w={320} disabled={!cash.suggestWhy} label={cash.suggestWhy}>
        <span><Button size="xs" variant="light" disabled={!!cash.suggestWhy} onClick={cash.onSuggest}>{cash.suggestLabel}</Button></span>
      </Tooltip>
      <Text size="xs" c="dimmed">or</Text>
      <Checkbox size="xs" color="dark" checked={cash.accepted} onChange={(e) => cash.onAccept(e.currentTarget.checked)}
        label="I accept the cash as it is" />
    </>);
  } else {
    next = save.why ? <Text size="xs" c="dimmed">{save.why}</Text>
      : <Text size="xs" c="green.8" fw={600}>Ready: Save run (or Save as a run) on the trade bar.</Text>;
  }

  // The banner takes the colour of what is open (Pedro 2026-11-01: "Can this banner at the top, or elements
  // of it, be red?"): red while something must be fixed, orange while something must be checked.
  const tone = (step === 1 && sec.items.length > 0) || (step === 3 && cash.ready && cash.hard.length > 0 && !cash.accepted) ? 'red'
    : step === 3 && cash.ready && cashN > 0 && !cash.accepted ? 'orange' : 'blue';
  return (
    <Paper withBorder px={6} py={4} radius="sm"
      style={{ borderColor: `var(--mantine-color-${tone}-${tone === 'blue' ? 3 : 5})`, borderWidth: tone === 'blue' ? 1 : 2,
        background: tone === 'blue' ? undefined : `var(--mantine-color-${tone}-0)` }}>
      <Group gap={6} wrap="nowrap">
        {titles.map((t, i) => {
          const s = statuses[i];
          const on = step === i + 1;
          return (
            <UnstyledButton key={t} onClick={() => onStep(i + 1)} title={`Step ${i + 1}: ${t}`}
              style={{ padding: '2px 6px', borderRadius: 4, flex: 'none',
                background: on ? 'var(--mantine-color-blue-0)' : undefined, border: `1px solid ${on ? 'var(--mantine-color-blue-4)' : 'transparent'}` }}>
              <Group gap={4} wrap="nowrap">
                <Text size="xs" fw={700}>{i + 1} · {t}</Text>
                <Badge size="xs" color={s.color} variant={s.done ? 'filled' : 'light'} style={{ textTransform: 'none' }}
                  leftSection={s.done ? <IconCheck size={9} /> : undefined}>{s.label}</Badge>
              </Group>
            </UnstyledButton>
          );
        })}
        <Text size="xs" fw={700} c={`${tone}.8`} style={{ flex: 'none' }}>▸ Next:</Text>
        <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>{next}</Group>
      </Group>
      {actions && <Group gap={8} mt={4} pl={4} wrap="wrap" align="center">{actions}</Group>}
      {step === 3 && cash.ready && cash.plan.length > 0 && (
        <Box mt={4} px={4}>
          <Text size="xs" fw={700} c="blue.8">
            The model plan's {cash.plan.length} cash transfer{cash.plan.length === 1 ? '' : 's'} (not applied yet — the issues above are without them):
          </Text>
          {cash.plan.map((m) => <Text key={m} size="xs" c="blue.8" pl={8}>{m}</Text>)}
        </Box>
      )}
      {open && listN > 1 && (
        <ScrollArea.Autosize mah={110} mt={4} type="auto">
          {/* pr: room for the scrollbar, so the Show buttons are never under it (smoke 2026-11-02, A2) */}
          <Stack gap={2} pl={4} pr={18}>
            {step === 1 && sec.items.map((it) => (
              <Group key={it.msg} gap={6} wrap="nowrap">
                <Text size="xs" c="red.8" style={{ flex: 1 }}>{it.msg}</Text>
                {it.key && <Button size="compact-xs" variant="light" onClick={() => it.key && sec.onShow(it.key)}>Show</Button>}
              </Group>
            ))}
            {step === 3 && [...cash.hard, ...cash.soft].map((m) => (
              <Box key={m}><Text size="xs" c={cash.hard.includes(m) ? 'red.8' : 'orange.8'}>{m}</Text></Box>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Paper>
  );
}
